import React, {useState} from 'react';
import {
    Show,
    useRecordContext,
    useRedirect,
    useTranslate,
    usePermissions,
    useUpdate,
    useNotify,
} from 'react-admin';
import {Alert, AlertTitle, Avatar, Box, Dialog, Link as MuiLink, Switch, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {
    AddOutlined,
    AutoFixHighOutlined,
    DeleteOutline,
    EditOutlined,
    OpenInNew,
    PersonAddAltOutlined,
    VisibilityOutlined,
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import StatamicTabs, {useTabs} from '../components/statamic/StatamicTabs';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import ProfileImageCard from '../components/ProfileImageCard';
import ChildTranslationTable from './ChildTranslationTable';
import {axiosApiInstance} from '../AxiosUtils';
import hasPermission from '../auth/hasPermission';

const formatDate = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const ageFromDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    const years = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
    return years;
};

const MetaRow = ({label, children}) => (
    <Box sx={{display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'baseline', gap: 1.5, py: 0.5}}>
        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500}}>
            {label}
        </Typography>
        <Box sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, minWidth: 0}}>
            {children || <Box component="span" sx={{color: t.color.text.muted}}>—</Box>}
        </Box>
    </Box>
);

const VerifiedToggle = ({record, translate}) => {
    const [update, {isPending}] = useUpdate();
    const notify = useNotify();
    const checked = !!record.verified;

    const handleChange = (e) => {
        const next = e.target.checked;
        update(
            'children',
            {id: record.id, data: {...record, verified: next}, previousData: record},
            {
                mutationMode: 'optimistic',
                onSuccess: () => notify(next ? 'Child verified' : 'Child unverified', {type: 'info'}),
                onError: (err) => notify(`Failed to update verified status: ${err?.message ?? ''}`, {type: 'error'}),
            }
        );
    };

    const palette = checked
        ? {bg: t.color.status.successTint, fg: '#047857'}
        : {bg: t.color.status.neutralTint, fg: '#374151'};

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                pl: 0.5,
                pr: 1,
                borderRadius: `${t.radius.sm}px`,
                backgroundColor: palette.bg,
                color: palette.fg,
                fontSize: `${t.type.caption.size}px`,
                fontWeight: 500,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
            }}
        >
            <Switch
                size="small"
                checked={checked}
                disabled={isPending}
                onChange={handleChange}
                sx={{
                    m: 0,
                    '& .MuiSwitch-switchBase.Mui-checked': {color: '#047857'},
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {backgroundColor: '#047857'},
                }}
            />
            <Box component="span">
                {checked ? translate('Verified') : translate('Not verified')}
            </Box>
        </Box>
    );
};

const IdentityCard = ({record, photoUrl, translate, childDetailUrl, canEditVerified}) => {
    const fullName = [record.first_name, record.last_name].filter(Boolean).join(' ') || 'Unnamed';
    const age = ageFromDate(record.birth_date);
    const gender = record.gender
        ? record.gender[0] + record.gender.slice(1).toLowerCase()
        : null;

    const facts = [
        age != null ? `Age ${age}` : null,
        gender,
        record.adoption_number ? `Adoption #${record.adoption_number}` : null,
    ].filter(Boolean);

    return (
        <Box
            sx={{
                backgroundColor: t.color.surface,
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.md}px`,
                p: 3,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 4,
                alignItems: 'stretch',
            }}
        >
            <Box sx={{display: 'flex', gap: 3, alignItems: 'center', minWidth: 0}}>
                <Avatar
                    src={photoUrl}
                    sx={{
                        width: 120,
                        height: 120,
                        fontSize: 36,
                        flexShrink: 0,
                        bgcolor: t.color.sidebarHover,
                        color: t.color.text.secondary,
                        border: `1px solid ${t.color.border.hairline}`,
                    }}
                >
                    {(record.first_name?.charAt(0) ?? '') + (record.last_name?.charAt(0) ?? '')}
                </Avatar>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: t.color.text.primary,
                            lineHeight: 1.2,
                        }}
                    >
                        {fullName}
                    </Typography>
                    {facts.length > 0 && (
                        <Typography sx={{mt: 0.5, fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}>
                            {facts.join(' · ')}
                        </Typography>
                    )}
                    <Box sx={{display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap', alignItems: 'center'}}>
                        {canEditVerified ? (
                            <VerifiedToggle record={record} translate={translate}/>
                        ) : (
                            <StatusPill variant={record.verified ? 'success' : 'neutral'}>
                                {record.verified ? translate('Verified') : translate('Not verified')}
                            </StatusPill>
                        )}
                        <StatusPill variant={record.child_is_sponsored ? 'success' : 'warning'}>
                            {record.child_is_sponsored ? translate('Sponsored') : translate('Unsponsored')}
                        </StatusPill>
                        {record.project_level && (
                            <StatusPill variant="info">{translate('Project level')}</StatusPill>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pl: 3,
                    borderLeft: `1px solid ${t.color.border.hairline}`,
                }}
            >
                <MetaRow label={translate('Birth date')}>
                    {formatDate(record.birth_date)}
                </MetaRow>
                <MetaRow label={translate('Charity')}>
                    {(record.charity_external_id || record.charity_id) ? (
                        <MuiLink
                            component={RouterLink}
                            to={`/charities/${record.charity_external_id ?? record.charity_id}/show`}
                            sx={{color: t.color.accent.primary, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                        >
                            {record.charity_name || `Charity #${record.charity_id ?? record.charity_external_id}`}
                        </MuiLink>
                    ) : record.charity_name ? (
                        record.charity_name
                    ) : null}
                </MetaRow>
                <MetaRow label={translate('Contact frequency')}>
                    {record.contact_frequency ? translate(record.contact_frequency) : null}
                </MetaRow>
                {childDetailUrl && (
                    <MetaRow label={translate('Online')}>
                        <MuiLink
                            href={childDetailUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: t.color.accent.primary,
                                textDecoration: 'none',
                                '&:hover': {textDecoration: 'underline'},
                            }}
                        >
                            {translate('View online child details page')}
                            <OpenInNew sx={{fontSize: 13}}/>
                        </MuiLink>
                    </MetaRow>
                )}
            </Box>
        </Box>
    );
};

const GuardianCard = ({guardian}) => (
    <Box
        component={RouterLink}
        to={`/guardians/${guardian.id}/show`}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: `${t.radius.md}px`,
            border: `1px solid ${t.color.border.default}`,
            textDecoration: 'none',
            color: 'inherit',
            transition: 'border-color 120ms ease, background-color 120ms ease',
            '&:hover': {
                borderColor: t.color.border.strong,
                backgroundColor: t.color.sidebarHover,
            },
        }}
    >
        <Avatar
            src={guardian.avatar ? `${process.env.REACT_APP_CDN_URL}/${guardian.avatar}` : undefined}
            sx={{
                width: 40, height: 40,
                bgcolor: t.color.sidebarHover,
                color: t.color.text.secondary,
                border: `1px solid ${t.color.border.hairline}`,
            }}
        />
        <Box sx={{minWidth: 0, flex: 1}}>
            <Typography
                sx={{
                    fontSize: `${t.type.bodyStrong.size}px`,
                    fontWeight: t.type.bodyStrong.weight,
                    color: t.color.text.primary,
                }}
            >
                {guardian.given_name} {guardian.family_name}
            </Typography>
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                Sponsor
            </Typography>
        </Box>
    </Box>
);

const SponsorPostsActions = ({record, translate}) => {
    const navigate = useNavigate();
    return (
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
            <PrimaryButton
                variant="secondary"
                startIcon={<VisibilityOutlined sx={{fontSize: 16}}/>}
                onClick={() => navigate(`/posts?filter={"child_id":"${record.id}"}`)}
            >
                {translate('View posts')}
            </PrimaryButton>
            <PrimaryButton
                variant="primary"
                startIcon={<AddOutlined sx={{fontSize: 16}}/>}
                onClick={() => navigate('/posts/create', {state: {child: record}})}
            >
                {translate('Share new post')}
            </PrimaryButton>
        </Box>
    );
};

const OptimizeConfirmDialog = ({open, onClose, onConfirm, pending, translate}) => (
    <Dialog
        open={open}
        onClose={pending ? undefined : onClose}
        slotProps={{
            paper: {
                sx: {
                    borderRadius: `${t.radius.lg}px`,
                    border: `1px solid ${t.color.border.default}`,
                    boxShadow: t.shadow.modal,
                    maxWidth: 440,
                },
            },
        }}
    >
        <Box sx={{p: 3}}>
            <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start'}}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: t.color.status.neutralTint,
                        color: t.color.text.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <AutoFixHighOutlined sx={{fontSize: 20}}/>
                </Box>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography
                        sx={{
                            fontSize: `${t.type.h3.size}px`,
                            fontWeight: t.type.h3.weight,
                            color: t.color.text.primary,
                            lineHeight: 1.3,
                        }}
                    >
                        {translate('Optimize profile text')}
                    </Typography>
                    <Typography
                        sx={{
                            mt: 1,
                            fontSize: `${t.type.small.size}px`,
                            color: t.color.text.secondary,
                            lineHeight: 1.5,
                        }}
                    >
                        {translate('This rewrites the story fields with AI and marks the child verified. If the AI service is unavailable the fields can end up empty. Continue?')}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3}}>
                <PrimaryButton variant="secondary" onClick={onClose} disabled={pending}>
                    {translate('ra.action.cancel')}
                </PrimaryButton>
                <PrimaryButton onClick={onConfirm} disabled={pending}>
                    {pending ? '…' : translate('Continue')}
                </PrimaryButton>
            </Box>
        </Box>
    </Dialog>
);

const OptimizeProfileAction = ({record, translate}) => {
    const [optimizing, setOptimizing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [status, setStatus] = useState(null);
    const [errorText, setErrorText] = useState('');

    const optimize = () => {
        if (!record) return;
        setOptimizing(true);
        setStatus(null);
        setErrorText('');
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v2/admin/children/${record.id}/optimize`
        ).then((response) => {
            setOptimizing(false);
            setConfirmOpen(false);
            setStatus(response.status === 200 ? 'success' : 'error');
        }).catch((reason) => {
            setOptimizing(false);
            setConfirmOpen(false);
            setStatus('error');
            setErrorText(typeof reason === 'string' ? reason : (reason?.message ?? ''));
        });
    };

    return (
        <SectionCard
            title={translate('Profile tools')}
            subtitle={translate('Generate/refresh translations and content via OpenAI')}
        >
            <LoadingButton
                onClick={() => setConfirmOpen(true)}
                loading={optimizing}
                variant="outlined"
                size="small"
                startIcon={<AutoFixHighOutlined sx={{fontSize: 16}}/>}
                sx={{
                    textTransform: 'none',
                    fontSize: `${t.type.small.size}px`,
                    borderColor: t.color.border.default,
                    color: t.color.text.primary,
                    '&:hover': {borderColor: t.color.accent.primary, color: t.color.accent.primary, backgroundColor: 'transparent'},
                }}
            >
                {translate('Optimize child profile')}
            </LoadingButton>
            {status === 'success' && (
                <Alert severity="success" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Success</AlertTitle>
                    {translate('Child profile optimized')}
                </Alert>
            )}
            {status === 'error' && (
                <Alert severity="warning" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Warning</AlertTitle>
                    {errorText || translate('Could not optimize profile.')}
                </Alert>
            )}
            <OptimizeConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={optimize}
                pending={optimizing}
                translate={translate}
            />
        </SectionCard>
    );
};

const DetailsTab = ({record, translate, permissions}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <SectionCard title={translate('Child story')}>
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3}}>
                <DataRow label={translate('What do I dream of? What makes me happy?')} value={record.intro}/>
                <DataRow label={translate('My story')} value={record.family}/>
            </Box>
            <Box sx={{mt: 1}}>
                <DataRow label={translate('Why do I need help')} value={record.help}/>
            </Box>
        </SectionCard>

        {record.child_is_sponsored && (
            <SectionCard
                title={translate('Sponsors')}
                subtitle={record.guardians?.length
                    ? `${record.guardians.length} guardian${record.guardians.length === 1 ? '' : 's'}`
                    : undefined}
                actions={<SponsorPostsActions record={record} translate={translate}/>}
            >
                {record.guardians?.length > 0 ? (
                    <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1}}>
                        {record.guardians.map(g => <GuardianCard key={g.id} guardian={g}/>)}
                    </Box>
                ) : (
                    <Typography sx={{color: t.color.text.muted, fontStyle: 'italic', fontSize: `${t.type.small.size}px`}}>
                        {translate('\'Guardian(s)')}
                    </Typography>
                )}
            </SectionCard>
        )}

        {hasPermission(permissions, ['show'], 'umbrella') && (
            <OptimizeProfileAction record={record} translate={translate}/>
        )}
    </Box>
);

const TranslationsTab = ({record, translate}) => (
    <SectionCard title={translate('Translations')}>
        <ChildTranslationTable translations={record.translations}/>
    </SectionCard>
);

const MediaTab = ({record, translate, permissions}) => {
    const videoUrl = record.profile_video && record.video_ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.profile_video}.${record.video_ext}`
        : null;
    const videoThumb = record.profile_video_thumbnail
        ? `${process.env.REACT_APP_CDN_URL}/${record.profile_video_thumbnail}`
        : null;
    const imageUrl = record.profile_image && record.image_ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.profile_image}.${record.image_ext}`
        : null;

    return (
        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start'}}>
            {imageUrl ? (
                <ProfileImageCard record={record} permissions={permissions}/>
            ) : (
                <SectionCard title={translate('Profile image')}>
                    <Typography sx={{color: t.color.text.muted, fontStyle: 'italic', textAlign: 'center', py: 3, fontSize: `${t.type.small.size}px`}}>
                        No profile image uploaded
                    </Typography>
                </SectionCard>
            )}
            <SectionCard title={translate('Introduction video')}>
                {videoUrl ? (
                    <Box sx={{borderRadius: `${t.radius.md}px`, overflow: 'hidden', border: `1px solid ${t.color.border.hairline}`}}>
                        <ReactPlayer
                            url={videoUrl}
                            light={videoThumb}
                            controls
                            width="100%"
                            height="320px"
                        />
                    </Box>
                ) : (
                    <Typography sx={{color: t.color.text.muted, fontStyle: 'italic', textAlign: 'center', py: 3, fontSize: `${t.type.small.size}px`}}>
                        No introduction video uploaded
                    </Typography>
                )}
            </SectionCard>
        </Box>
    );
};

const ChildShowContent = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const navigate = useNavigate();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const [tab, setTab] = useTabs('details');
    const deleteConfirm = useDeleteConfirm();

    if (!record) return null;

    const photoUrl = record.profile_image && record.image_ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.profile_image}.${record.image_ext}`
        : record.profile_image_thumbnail
            ? `${process.env.REACT_APP_CDN_URL}/${record.profile_image_thumbnail}`
            : undefined;

    const fullName = [record.first_name, record.last_name].filter(Boolean).join(' ') || 'Unnamed';

    const childDetailUrl = record.child_detail_url || null;

    const tabs = [
        {id: 'details',      label: translate('Details')},
        {id: 'media',        label: translate('Media')},
        {id: 'translations', label: translate('Translations')},
    ];

    const breadcrumbTrail = (() => {
        const trail = [];
        if (record.parent_charity_id && record.parent_charity_name) {
            trail.push({label: translate('Umbrellas'), to: '/umbrellas'});
            trail.push({label: record.parent_charity_name, to: `/umbrellas/${record.parent_charity_id}/show`});
        }
        if ((record.charity_external_id || record.charity_id) && record.charity_name) {
            trail.push({label: translate('Charities'), to: '/charities'});
            trail.push({label: record.charity_name, to: `/charities/${record.charity_external_id ?? record.charity_id}/show`});
        }
        trail.push({label: translate('Children'), to: '/children'});
        trail.push({label: fullName});
        return trail.length > 2 ? trail : null;
    })();

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={breadcrumbTrail ?? {label: translate('Children'), to: '/children'}}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record.verified ? 'success' : 'neutral'}/>
                        <Box component="span">{fullName}</Box>
                    </Box>
                }
                actions={
                    <>
                        {hasPermission(permissions, ['match'], 'guardians') && !record.child_is_sponsored && (
                            <PrimaryButton
                                variant="secondary"
                                startIcon={<PersonAddAltOutlined sx={{fontSize: 16}}/>}
                                onClick={() => navigate('/guardians/create', {state: {child: record}})}
                            >
                                {translate('Add New Sponsor')}
                            </PrimaryButton>
                        )}
                        <PrimaryButton
                            variant="primary"
                            startIcon={<EditOutlined sx={{fontSize: 16}}/>}
                            onClick={() => redirect('edit', 'children', record.external_id || record.id)}
                        >
                            {translate('Edit')}
                        </PrimaryButton>
                        {hasPermission(permissions, ['delete'], 'children.delete') && !record.child_is_sponsored && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            <StatamicTabs tabs={tabs} value={tab} onChange={setTab}/>

            <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
                <IdentityCard
                    record={record}
                    photoUrl={photoUrl}
                    translate={translate}
                    childDetailUrl={childDetailUrl}
                    canEditVerified={hasPermission(permissions, ['create', 'edit'], 'children.verified')}
                />


                {tab === 'details' && <DetailsTab record={record} translate={translate} permissions={permissions}/>}
                {tab === 'media'   && <MediaTab record={record} translate={translate} permissions={permissions}/>}
                {tab === 'translations' && <TranslationsTab record={record} translate={translate}/>}
            </Box>

            <DeleteConfirmDialog
                open={deleteConfirm.open}
                onClose={deleteConfirm.closeDialog}
                resource="children"
                record={record}
                recordLabel={fullName}
            />
        </Box>
    );
};

const ChildShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <ChildShowContent/>
    </Show>
);

export default ChildShowStatamic;
